/**
 * `editorPerf` — a dev-only performance probe for the editor's hot paths (Phase 0,
 * 2026-06-10).
 *
 * Records the wall-clock cost of the markdown-emit (the formerly-per-keystroke
 * `getHTML()` + turndown serialization) and the decorator re-scans, and paints a tiny
 * fixed-position badge with rolling p50/p95 so the debounce/decorator work can be measured
 * before/after on a large stress note.
 *
 * Zero overhead in production: every entry point early-returns unless this is a DEV build
 * AND the overlay was opted into via `localStorage.setItem('aletheia.perfOverlay','1')`.
 * The badge is a single imperatively-managed DOM node (no React, no re-renders).
 */

const MAX_SAMPLES = 60;

interface Channel {
  label: string;
  samples: number[];
}

const channels: Record<string, Channel> = {};
let badge: HTMLDivElement | null = null;
let enabledCache: boolean | null = null;

function isEnabled(): boolean {
  if (enabledCache !== null) return enabledCache;
  let on = false;
  try {
    on = import.meta.env.DEV && localStorage.getItem('aletheia.perfOverlay') === '1';
  } catch {
    on = false;
  }
  enabledCache = on;
  return on;
}

function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  const idx = Math.min(sortedAsc.length - 1, Math.floor((p / 100) * sortedAsc.length));
  return sortedAsc[idx];
}

/** Record a duration (ms) under a named channel ("emit", "decorate", …). Dev-only. */
export function recordPerf(channel: string, label: string, ms: number): void {
  if (!isEnabled()) return;
  const ch = channels[channel] ?? (channels[channel] = { label, samples: [] });
  ch.samples.push(ms);
  if (ch.samples.length > MAX_SAMPLES) ch.samples.shift();
  render();
}

/** Time a synchronous function and record it under `channel`. Returns its result. */
export function measurePerf<T>(channel: string, label: string, fn: () => T): T {
  if (!isEnabled()) return fn();
  const t0 = performance.now();
  try {
    return fn();
  } finally {
    recordPerf(channel, label, performance.now() - t0);
  }
}

function render(): void {
  if (typeof document === 'undefined') return;
  if (badge === null) {
    badge = document.createElement('div');
    badge.setAttribute('data-aletheia-perf', '');
    Object.assign(badge.style, {
      position: 'fixed',
      bottom: '8px',
      right: '8px',
      zIndex: '99999',
      font: '11px/1.5 ui-monospace, monospace',
      color: '#9fe',
      background: 'rgba(0,0,0,0.78)',
      border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: '6px',
      padding: '6px 9px',
      pointerEvents: 'none',
      whiteSpace: 'pre',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(badge);
  }
  const lines: string[] = [];
  for (const key of Object.keys(channels)) {
    const ch = channels[key];
    const sorted = [...ch.samples].sort((a, b) => a - b);
    lines.push(
      `${ch.label.padEnd(9)} p50 ${percentile(sorted, 50).toFixed(1)}ms · ` +
        `p95 ${percentile(sorted, 95).toFixed(1)}ms · n${ch.samples.length}`,
    );
  }
  badge.textContent = lines.join('\n');
}
